<template>
    <div>
        <b-button
            v-if="!dropdownItems.length"
            :id="id"
            v-bind="passOnProps"
            :class="[customClasses, {loading, hover}]"
            @click="click($event)"
        >
            <template
                v-if="loading"
            >
                <FontAwesomeIcon
                    icon="spinner"
                    class="fa fa-pulse fa-lg"
                />
            </template>

            <!-- content to add before the button text when not in a loading state -->
            <slot
                v-else
                name="prepend"
            >
                <!-- Button types: confirmation = `far check-double` -->
                <FontAwesomeIcon
                    v-if="variant === 'confirmation' && !noIcon"
                    :icon="['far', 'check-double']"
                    class="fa btn-icon-right"
                />
            </slot>

            <!-- the button content when not in a loading state -->
            <slot
                v-if="!loading"
            />

            <!-- content to add after the button text when not in a loading state -->
            <slot
                v-if="!loading"
                name="append"
            />
        </b-button>
        <b-dropdown
            v-else
            ref="dropdown"
            v-bind="passOnDropdownProps"
            :class="customClasses"
            :disabled="disabled || loading"
            right
            no-caret
            :toggle-class="[{loading, hover, 'active': pressed}]"
        >
            <template
                slot="button-content"
            >
                <template
                    v-if="loading"
                >
                    <FontAwesomeIcon
                        icon="spinner"
                        class="fa fa-pulse fa-lg"
                    />
                </template>

                <!-- the button content when not in a loading state -->
                <slot
                    v-else
                />
                <FontAwesomeIcon
                    v-if="!loading"
                    :icon="['far', 'angle-down']"
                    class="fa btn-icon-left"
                />
            </template>

            <!-- the dropdown content to render instead of the default dropdown items -->
            <slot
                name="dropdown-content"
            >
                <b-dropdown-item
                    v-for="(item, index) in dropdownItems"
                    :key="index"
                    :href="item.href"
                    :class="item.class"
                    @click="item.callback ? item.callback : click($event, 'dropdown-item:')"
                >
                    {{ item.text }}
                </b-dropdown-item>
            </slot>
        </b-dropdown>
    </div>
</template>

<script>
    import {library} from '@fortawesome/fontawesome-svg-core';
    import {faCheckDouble, faAngleDown} from '@fortawesome/pro-regular-svg-icons';
    import {faSpinner} from '@fortawesome/pro-solid-svg-icons';
    import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome';
    import uniqueId from 'lodash/uniqueId';

    library.add(faCheckDouble, faAngleDown, faSpinner);

    /**
     * @group Buttons
     * A component to utilize a Kit Car button variant.
     */
    export default {
        name: 'Button',
        components: {
            FontAwesomeIcon,
        },
        props: {
            /**
             * Custom classes for the actual `button` tag.
             */
            customClasses: {
                type: [Array, Object],
                required: false,
                // `{}`
                default() {
                    return {};
                },
            },
            /**
             * Denotes the target URL of the link for standard a links
             */
            href: {
                type: String,
                required: false,
                // &mdash;
                default: null,
            },
            /**
             * Sets the 'id' attribute on the button
             */
            id: {
                type: String,
                default: () => {
                    return uniqueId();
                }
            },
            /**
             * Sets the 'rel' attribute on the rendered link
             */
            rel: {
                type: String,
                required: false,
                // &mdash;
                default: null,
            },
            /**
             * Sets the 'target' attribute on the rendered link
             */
            target: {
                type: String,
                required: false,
                // `'_self'`
                default: '_self',
            },
            /**
             * When set to `true`, places the component in the active state with active styling
             */
            active: {
                type: Boolean,
                required: false,
            },
            /**
             * When set to `true`, disables the component's functionality and places it in a disabled state
             */
            disabled: {
                type: Boolean,
                required: false,
            },
            /**
             * `router-link` prop: Denotes the target route of the link. When clicked, the value of the to prop will be passed to `router.push()` internally, so the value can be either a string or a `Location` descriptor object
             */
            to: {
                type: [String, Object],
                required: false,
                // &mdash;
                default: null,
            },
            /**
             * `router-link` prop: Setting append prop always appends the relative path to the current path
             */
            append: {
                type: Boolean,
                required: false,
            },
            /**
             * `router-link` prop: Setting the replace prop will call `router.replace()` instead of `router.push()` when clicked, so the navigation will not leave a history record
             */
            replace: {
                type: Boolean,
                required: false,
            },
            /**
             * `router-link` prop: Specify the event that triggers the link. In most cases you should leave this as the default
             */
            event: {
                type: [String, Array],
                required: false,
                // `click`
                default: 'click',
            },
            /**
             * `router-link` prop: Configure the active CSS class applied when the link is active. Typically you will want to set this to class name `active`
             */
            activeClass: {
                type: String,
                required: false,
                // &mdash;
                default: null,
            },
            /**
             * `router-link` prop: The default active class matching behavior is inclusive match. Setting this prop forces the mode to exactly match the route
             */
            exact: {
                type: Boolean,
                required: false,
            },
            /**
             * `router-link` prop: Configure the active CSS class applied when the link is active with exact match. Typically you will want to set this to class name `active`
             */
            exactActiveClass: {
                type: String,
                required: false,
                // &mdash;
                default: null,
            },
            /**
             * `router-link` prop: Specify which tag to render, and it will still listen to click events for navigation. `router-tag` translates to the tag prop on the final rendered router-link. Typically you should use the default value
             */
            routerTag: {
                type: String,
                required: false,
                // `'a'`
                default: 'a',
            },
            /**
             * Renders a 100% width button (expands to the width of it's parent container)
             */
            block: {
                type: Boolean,
                required: false,
            },
            /**
             * Set the size of the component's appearance. `sm`, `md` (default), or `lg`
             */
            size: {
                // `sm` / `md` / `lg`
                type: String,
                required: false,
                // `md`
                default: 'md'
            },
            /**
             * Applies one of the Kit Car theme color variants to the component
             */
            variant: {
                // `primary` / `secondary` / `tertiary` / `page-specific` / `internal-only` / `edit` / `destruction` / `confirmation` / `action`
                type: String,
                required: false,
                // `secondary`
                default: 'secondary'
            },
            /**
             * The value to set the button's `type` attribute to. Can be one of `button`, `submit`, or `reset`
             */
            type: {
                // `button` / `submit` / `reset`
                type: String,
                required: false,
                // `button`
                default: 'button',
            },
            /**
             * Specify the HTML tag to render instead of the default tag
             */
            tag: {
                type: String,
                required: false,
                // `button`
                default: 'button'
            },
            /**
             * When set to `true`, gives the button the appearance of being pressed. Syncable with the `.sync` modifier
             */
            pressed: {
                type: Boolean,
                required: false,
            },
            /**
             * When set to `true`, disables the component's functionality and places it in a loading state
             */
            loading: {
                type: Boolean,
                required: false,
                default: false,
            },
            /**
             * When set to `true`, disables any icons that would appear in the button with the exception of dropdown carets
             */
            noIcon: {
                type: Boolean,
                required: false,
                default: false,
            },
            /**
             * When set to `true`, gives the button the appearance of being hovered over
             */
            hover: {
                type: Boolean,
                required: false,
                default: false,
            },
            /**
             * An array of dropdown items to display under the button click. Renders button with dropdown and the provided items within it. Expects objects with keys like so: `{text: '', [href]: '', [class]: '', [callback]() {}` (`[key]` denotes it's optional).
             */
            dropdownItems: {
                type: Array,
                required: false,
                // `[]`
                default() {
                    return [];
                },
            },
        },
        data() {
            return {
                dropdownOpen: this.pressed,
            };
        },
        computed: {
            passOnProps() {
                let {
                    loading, // eslint-disable-line no-unused-vars
                    dropdownItems, // eslint-disable-line no-unused-vars
                    ...propsForBootstrapVue
                } = this.$props;

                return propsForBootstrapVue;
            },
            passOnDropdownProps() {
                let {
                    loading, // eslint-disable-line no-unused-vars
                    dropdownItems, // eslint-disable-line no-unused-vars
                    type, // eslint-disable-line no-unused-vars
                    tag, // eslint-disable-line no-unused-vars
                    ...propsForBootstrapVue
                } = this.$props;

                return propsForBootstrapVue;
            },
        },
        methods: {
            click($event, eventPrefix = '') {
                if (!this.loading && !this.disabled) {
                    // Emitted when the button (or eventPrefix'd element is clicked - e.g. `dropdown-item`) is clicked and it is not in a disabled or loading state.
                    this.$emit(`${eventPrefix}click`, $event);
                }
            },
        },
    };
</script>
